import { useTranslation } from 'react-i18next';

import { FcmNotificationType } from '@/modules/notifications';

import { NOTIFICATION_TYPE_CONFIG } from '../constants/notification-type-config';
import { isTabFocused } from '../utils/tab-focus';
import { useNotifications } from './use-notifications';

/**
 * Shows native browser notifications for non-silent NOTIFICATION_CREATED FCM messages
 * when the app tab is not focused.
 *
 * Derives display text from notificationType + NOTIFICATION_TYPE_CONFIG i18n keys.
 * Uses `Notification.tag` to deduplicate across multiple open tabs.
 * Must be mounted once in `AuthenticatedTemplate`.
 */
export const useVisibleNotifications = (): void => {
  const { t } = useTranslation('authenticated.notifications');

  useNotifications(FcmNotificationType.NOTIFICATION_CREATED, notification => {
    if (notification.silent) return;
    if (isTabFocused()) return;

    const notificationType = notification.data.notificationType as string | undefined;
    const argsRaw = notification.data.args as string | undefined;
    const args: Record<string, string> = argsRaw ? JSON.parse(argsRaw) : {};

    const typeConfig = notificationType ? NOTIFICATION_TYPE_CONFIG[notificationType] : null;
    if (!typeConfig) return;

    const title = t(typeConfig.titleKey, args);
    const body = t(typeConfig.descriptionKey, args);

    new Notification(`[Opsium] ${title}`, {
      body,
      tag: notification.id,
      icon: '/opsium-logo.png',
    });
  });
};
